/**
 * Library Service - Funkcje do zarządzania biblioteką
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type { 
  Book, 
  Author, 
  BookAuthor, 
  BookFile, 
  BookWithAuthors,
  BookTag,
  BookCategory,
  BookWithTagsAndCategories,
  BookCategoryWithParent
} from '../types/database.types';

// ========================================
// TYPES
// ========================================

export type { Book, Author, BookAuthor, BookFile, BookWithAuthors, BookTag, BookCategory, BookWithTagsAndCategories };

export interface CreateBookData {
  title: string;
  description?: string;
  summary?: string;
  external_link?: string;
  cover_image_url?: string;
  isbn?: string;
  publication_year?: number;
  publisher?: string;
  language?: string;
  pages?: number;
  tag_ids?: string[]; // Nowe: IDs wybranych tagów
  category_ids?: string[]; // Nowe: IDs wybranych kategorii
  status?: 'draft' | 'published';
  authors?: Array<{ name: string; bio?: string; order: number }>; // Inline authors
}

export interface UpdateBookData extends Partial<CreateBookData> {
  id: string;
}

export interface CreateAuthorData {
  name: string;
  bio?: string | null;
  website_url?: string | null;
}

export interface UpdateAuthorData extends Partial<CreateAuthorData> {
  id: string;
}

// ========================================
// AUTHORS CRUD
// ========================================

/**
 * Pobiera wszystkich autorów
 */
export async function getAllAuthors(options?: {
  search?: string;
  includeBookCount?: boolean;
}): Promise<{ success: boolean; data?: Author[]; error?: string }> {
  try {
    let query = supabase
      .from('authors')
      .select('*')
      .order('name', { ascending: true });

    // Search by name or bio
    if (options?.search) {
      query = query.or(
        `name.ilike.%${options.search}%,bio.ilike.%${options.search}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching authors:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera autora po ID
 */
export async function getAuthorById(
  authorId: string
): Promise<{ success: boolean; data?: Author; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('authors')
      .select('*')
      .eq('id', authorId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Author not found');

    return { success: true, data };
  } catch (error: any) {
    console.error('Error fetching author:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Tworzy nowego autora
 */
export async function createAuthor(
  authorData: CreateAuthorData
): Promise<{ success: boolean; data?: Author; error?: string }> {
  try {
    // Sprawdź czy autor o tej nazwie już istnieje
    const { data: existing } = await supabase
      .from('authors')
      .select('id, name')
      .ilike('name', authorData.name)
      .single();

    if (existing) {
      return {
        success: false,
        error: `Autor "${authorData.name}" już istnieje w bazie danych.`,
      };
    }

    const { data, error } = await supabase
      .from('authors')
      .insert({
        name: authorData.name.trim(),
        bio: authorData.bio?.trim() || null,
        website_url: authorData.website_url?.trim() || null,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating author:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje autora
 */
export async function updateAuthor(
  authorData: UpdateAuthorData
): Promise<{ success: boolean; data?: Author; error?: string }> {
  try {
    const { id, ...updateFields } = authorData;

    const { data, error } = await supabase
      .from('authors')
      .update(updateFields)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('Author not found');

    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating author:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa autora (jeśli nie jest przypisany do żadnej książki)
 */
export async function deleteAuthor(
  authorId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Sprawdź czy autor ma przypisane książki
    const { data: bookAuthors } = await supabase
      .from('book_authors')
      .select('id')
      .eq('author_id', authorId)
      .limit(1);

    if (bookAuthors && bookAuthors.length > 0) {
      return {
        success: false,
        error: 'Nie można usunąć autora przypisanego do książek. Najpierw usuń powiązania z książkami.',
      };
    }

    const { error } = await supabase
      .from('authors')
      .delete()
      .eq('id', authorId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting author:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// BOOKS CRUD
// ========================================

/**
 * Tworzy nową książkę w bibliotece
 */
export async function createBook(
  bookData: CreateBookData,
  userId: string
): Promise<{ success: boolean; data?: Book; error?: string }> {
  try {
    const { authors, tag_ids, category_ids, ...bookFields } = bookData;

    // Validate authors
    if (!authors || authors.length === 0) {
      throw new Error('Książka musi mieć przynajmniej jednego autora');
    }

    // Insert book
    const { data: book, error: bookError } = await supabase
      .from('books')
      .insert({
        ...bookFields,
        created_by: userId,
        language: bookData.language || 'pl',
        status: bookData.status || 'draft',
      })
      .select()
      .single();

    if (bookError) throw bookError;
    if (!book) throw new Error('Failed to create book');

    // Create or get authors and insert book-author relations
    const authorPromises = authors.map(async (authorData, index) => {
      // Check if author exists
      const { data: existingAuthor } = await supabase
        .from('authors')
        .select('id')
        .ilike('name', authorData.name.trim())
        .single();

      let authorId: string;

      if (existingAuthor) {
        authorId = existingAuthor.id;
      } else {
        // Create new author
        const { data: newAuthor, error: authorError } = await supabase
          .from('authors')
          .insert({
            name: authorData.name.trim(),
            bio: authorData.bio?.trim() || null,
          })
          .select('id')
          .single();

        if (authorError || !newAuthor) {
          throw new Error(`Nie udało się utworzyć autora: ${authorData.name}`);
        }
        authorId = newAuthor.id;
      }

      return { authorId, order: authorData.order || index };
    });

    const authorResults = await Promise.all(authorPromises);

    // Insert book-author relations
    const bookAuthorsData = authorResults.map(({ authorId, order }) => ({
      book_id: book.id,
      author_id: authorId,
      author_order: order,
    }));

    const { error: authorsError } = await supabase
      .from('book_authors')
      .insert(bookAuthorsData);

    if (authorsError) {
      console.error('Error creating book-author relations:', authorsError);
      // Book is created, but authors failed - try to rollback
      await supabase.from('books').delete().eq('id', book.id);
      throw new Error('Nie udało się przypisać autorów do książki');
    }

    // Assign tags if provided
    if (tag_ids && tag_ids.length > 0) {
      await assignTagsToBook(book.id, tag_ids);
    }

    // Assign categories if provided
    if (category_ids && category_ids.length > 0) {
      await assignCategoriesToBook(book.id, category_ids);
    }

    return { success: true, data: book };
  } catch (error: any) {
    console.error('Error creating book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje książkę
 */
export async function updateBook(
  bookData: UpdateBookData
): Promise<{ success: boolean; data?: Book; error?: string }> {
  try {
    const { id, authors, tag_ids, category_ids, ...updateFields } = bookData;

    // Update book
    const { data: book, error: bookError } = await supabase
      .from('books')
      .update(updateFields)
      .eq('id', id)
      .select()
      .single();

    if (bookError) throw bookError;
    if (!book) throw new Error('Book not found');

    // Update authors if provided
    if (authors) {
      if (authors.length === 0) {
        throw new Error('Książka musi mieć przynajmniej jednego autora');
      }

      // Delete existing book-author relations
      await supabase.from('book_authors').delete().eq('book_id', id);

      // Create or get authors and insert book-author relations
      const authorPromises = authors.map(async (authorData, index) => {
        // Check if author exists
        const { data: existingAuthor } = await supabase
          .from('authors')
          .select('id')
          .ilike('name', authorData.name.trim())
          .single();

        let authorId: string;

        if (existingAuthor) {
          authorId = existingAuthor.id;
        } else {
          // Create new author
          const { data: newAuthor, error: authorError } = await supabase
            .from('authors')
            .insert({
              name: authorData.name.trim(),
              bio: authorData.bio?.trim() || null,
            })
            .select('id')
            .single();

          if (authorError || !newAuthor) {
            throw new Error(`Nie udało się utworzyć autora: ${authorData.name}`);
          }
          authorId = newAuthor.id;
        }

        return { authorId, order: authorData.order || index };
      });

      const authorResults = await Promise.all(authorPromises);

      // Insert new book-author relations
      const bookAuthorsData = authorResults.map(({ authorId, order }) => ({
        book_id: id,
        author_id: authorId,
        author_order: order,
      }));

      const { error: authorsError } = await supabase
        .from('book_authors')
        .insert(bookAuthorsData);

      if (authorsError) {
        console.error('Error updating book-author relations:', authorsError);
        throw new Error('Nie udało się zaktualizować autorów książki');
      }
    }

    // Update tags if provided
    if (tag_ids !== undefined) {
      await assignTagsToBook(id, tag_ids);
    }

    // Update categories if provided
    if (category_ids !== undefined) {
      await assignCategoriesToBook(id, category_ids);
    }

    return { success: true, data: book };
  } catch (error: any) {
    console.error('Error updating book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa książkę
 */
export async function deleteBook(bookId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from('books').delete().eq('id', bookId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera książkę po ID (z autorami)
 */
export async function getBookById(
  bookId: string
): Promise<{ success: boolean; data?: BookWithAuthors; error?: string }> {
  try {
    const { data: book, error: bookError } = await supabase
      .from('books')
      .select(
        `
        *,
        book_authors (
          author_order,
          authors (*)
        )
      `
      )
      .eq('id', bookId)
      .single();

    if (bookError) throw bookError;
    if (!book) throw new Error('Book not found');

    // Przekształć strukturę danych
    const result: BookWithAuthors = {
      ...book,
      authors: (book.book_authors || [])
        .map((ba: any) => ({
          ...ba.authors,
          author_order: ba.author_order,
        }))
        .sort((a: any, b: any) => a.author_order - b.author_order),
    };

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error fetching book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wszystkie książki (z opcjonalnymi filtrami)
 */
export async function getAllBooks(options?: {
  includeUnpublished?: boolean;
  search?: string;
}): Promise<{ success: boolean; data?: BookWithAuthors[]; error?: string }> {
  try {
    let query = supabase
      .from('books')
      .select(
        `
        *,
        book_authors (
          author_order,
          authors (*)
        )
      `
      )
      .order('created_at', { ascending: false });

    // Filter by status
    if (!options?.includeUnpublished) {
      query = query.eq('status', 'published');
    }

    // Search
    if (options?.search) {
      query = query.or(
        `title.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    // Przekształć strukturę danych
    const books: BookWithAuthors[] = (data || []).map((book: any) => ({
      ...book,
      authors: (book.book_authors || [])
        .map((ba: any) => ({
          ...ba.authors,
          author_order: ba.author_order,
        }))
        .sort((a: any, b: any) => a.author_order - b.author_order),
    }));

    return { success: true, data: books };
  } catch (error: any) {
    console.error('Error fetching books:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Publikuje książkę (zmienia status na 'published')
 */
export async function publishBook(bookId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('books')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
      })
      .eq('id', bookId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error publishing book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Archiwizuje książkę
 */
export async function archiveBook(bookId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('books')
      .update({ status: 'archived' })
      .eq('id', bookId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error archiving book:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// BOOK FILES MANAGEMENT
// ========================================

/**
 * Pobiera pliki książki
 */
export async function getBookFiles(
  bookId: string
): Promise<{ success: boolean; data?: BookFile[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('book_files')
      .select('*')
      .eq('book_id', bookId)
      .order('is_primary', { ascending: false })
      .order('uploaded_at', { ascending: false });

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching book files:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Dodaje plik do książki
 */
export async function addBookFile(
  bookId: string,
  fileData: {
    file_name: string;
    file_path: string;
    file_type: string;
    file_size: number;
    mime_type: string;
    is_primary?: boolean;
  },
  userId?: string
): Promise<{ success: boolean; data?: BookFile; error?: string }> {
  try {
    // Jeśli jest to primary file, ustaw wszystkie inne jako nie-primary
    if (fileData.is_primary) {
      await supabase
        .from('book_files')
        .update({ is_primary: false })
        .eq('book_id', bookId);
    }

    const { data, error } = await supabase
      .from('book_files')
      .insert({
        book_id: bookId,
        ...fileData,
        uploaded_by: userId || null,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error adding book file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa plik książki
 */
export async function deleteBookFile(
  fileId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Pobierz informacje o pliku
    const { data: file, error: fetchError } = await supabase
      .from('book_files')
      .select('file_path')
      .eq('id', fileId)
      .single();

    if (fetchError) throw fetchError;

    // Usuń z bazy danych
    const { error: deleteError } = await supabase
      .from('book_files')
      .delete()
      .eq('id', fileId);

    if (deleteError) throw deleteError;

    // Usuń z Storage (jeśli plik jest w Supabase Storage)
    if (file?.file_path && file.file_path.includes('library-files')) {
      try {
        const urlParts = new URL(file.file_path);
        const pathParts = urlParts.pathname.split('/');
        const storagePathIndex = pathParts.indexOf('library-files') + 1;
        const storagePath = pathParts.slice(storagePathIndex).join('/');

        await supabase.storage.from('library-files').remove([storagePath]);
      } catch (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Nie przerywa operacji - plik w bazie jest już usunięty
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting book file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje metadane pliku (np. is_primary)
 */
export async function updateBookFile(
  fileId: string,
  updates: {
    file_name?: string;
    is_primary?: boolean;
  }
): Promise<{ success: boolean; data?: BookFile; error?: string }> {
  try {
    // Jeśli ustawiamy jako primary, ustaw wszystkie inne jako nie-primary
    if (updates.is_primary) {
      const { data: file } = await supabase
        .from('book_files')
        .select('book_id')
        .eq('id', fileId)
        .single();

      if (file) {
        await supabase
          .from('book_files')
          .update({ is_primary: false })
          .eq('book_id', file.book_id);
      }
    }

    const { data, error } = await supabase
      .from('book_files')
      .update(updates)
      .eq('id', fileId)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating book file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Upload pliku do Supabase Storage
 */
export async function uploadFileToStorage(
  file: File,
  bookId?: string
): Promise<{ success: boolean; data?: { path: string; url: string }; error?: string }> {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop();
    const fileName = `${timestamp}_${randomStr}.${ext}`;
    const filePath = bookId ? `books/${bookId}/${fileName}` : `temp/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('library-files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('library-files')
      .getPublicUrl(filePath);

    return {
      success: true,
      data: {
        path: data.path,
        url: urlData.publicUrl,
      },
    };
  } catch (error: any) {
    console.error('Error uploading file:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa wszystkie pliki książki (używane przy usuwaniu książki)
 */
export async function deleteAllBookFiles(
  bookId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Pobierz wszystkie pliki
    const { data: files } = await supabase
      .from('book_files')
      .select('id, file_path')
      .eq('book_id', bookId);

    if (files && files.length > 0) {
      // Usuń każdy plik
      for (const file of files) {
        await deleteBookFile(file.id);
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting all book files:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// STATISTICS
// ========================================

/**
 * Pobiera statystyki biblioteki
 */
export async function getLibraryStats(): Promise<{
  success: boolean;
  data?: {
    totalBooks: number;
    publishedBooks: number;
    draftBooks: number;
    totalAuthors: number;
    categories: string[];
    tags: string[];
  };
  error?: string;
}> {
  try {
    const [
      { data: books, error: booksError },
      { data: authors, error: authorsError },
      { data: categoryRelations, error: categoryRelationsError },
      { data: tagRelations, error: tagRelationsError },
    ] = await Promise.all([
      supabase.from('books').select('id, status'),
      supabase.from('authors').select('id'),
      supabase.from('book_category_relations').select('book_categories(name)'),
      supabase.from('book_tag_relations').select('book_tags(name)'),
    ]);

    if (booksError) throw booksError;
    if (authorsError) throw authorsError;
    if (categoryRelationsError) throw categoryRelationsError;
    if (tagRelationsError) throw tagRelationsError;

    const publishedBooks = books?.filter((b) => b.status === 'published').length ?? 0;
    const draftBooks = books?.filter((b) => b.status === 'draft').length ?? 0;

    const categories = [
      ...new Set(
        (categoryRelations || [])
          .map((relation: any) => relation.book_categories?.name)
          .filter((name: string | null | undefined): name is string => Boolean(name))
      ),
    ] as string[];

    const tags = [
      ...new Set(
        (tagRelations || [])
          .map((relation: any) => relation.book_tags?.name)
          .filter((name: string | null | undefined): name is string => Boolean(name))
      ),
    ] as string[];

    return {
      success: true,
      data: {
        totalBooks: books?.length ?? 0,
        publishedBooks,
        draftBooks,
        totalAuthors: authors?.length ?? 0,
        categories,
        tags,
      },
    };
  } catch (error: any) {
    console.error('Error fetching library stats:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// TAGS & CATEGORIES CRUD
// ========================================

/**
 * Pobiera wszystkie tagi
 */
export async function getAllTags(options?: {
  search?: string;
  sortByUsage?: boolean;
}): Promise<{ success: boolean; data?: BookTag[]; error?: string }> {
  try {
    let query = supabase
      .from('book_tags')
      .select('*');

    // Search
    if (options?.search) {
      query = query.or(
        `name.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    // Sort
    if (options?.sortByUsage) {
      query = query.order('usage_count', { ascending: false });
    } else {
      query = query.order('name', { ascending: true });
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching tags:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Tworzy nowy tag
 */
export async function createTag(tagData: {
  name: string;
  description?: string;
  color?: string;
}): Promise<{ success: boolean; data?: BookTag; error?: string }> {
  try {
    // Sprawdź czy tag już istnieje
    const { data: existing } = await supabase
      .from('book_tags')
      .select('id, name')
      .ilike('name', tagData.name)
      .single();

    if (existing) {
      return {
        success: false,
        error: `Tag "${tagData.name}" już istnieje.`,
      };
    }

    const { data, error } = await supabase
      .from('book_tags')
      .insert({
        name: tagData.name.trim(),
        description: tagData.description?.trim() || null,
        color: tagData.color || '#E93D82',
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating tag:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje tag
 */
export async function updateTag(
  tagId: string,
  updates: {
    name?: string;
    description?: string;
    color?: string;
  }
): Promise<{ success: boolean; data?: BookTag; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('book_tags')
      .update(updates)
      .eq('id', tagId)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating tag:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa tag
 */
export async function deleteTag(tagId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('book_tags')
      .delete()
      .eq('id', tagId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting tag:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wszystkie kategorie
 */
export async function getAllCategories(options?: {
  search?: string;
  sortByUsage?: boolean;
  includeHierarchy?: boolean;
}): Promise<{ success: boolean; data?: BookCategory[] | BookCategoryWithParent[]; error?: string }> {
  try {
    let query = supabase
      .from('book_categories')
      .select(options?.includeHierarchy ? `*, parent:book_categories!parent_id(*)` : '*');

    // Search
    if (options?.search) {
      query = query.or(
        `name.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    // Sort
    if (options?.sortByUsage) {
      query = query.order('usage_count', { ascending: false });
    } else {
      query = query.order('display_order', { ascending: true });
      query = query.order('name', { ascending: true });
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching categories:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Tworzy nową kategorię
 */
export async function createCategory(categoryData: {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  parent_id?: string;
  display_order?: number;
}): Promise<{ success: boolean; data?: BookCategory; error?: string }> {
  try {
    // Sprawdź czy kategoria już istnieje
    const { data: existing } = await supabase
      .from('book_categories')
      .select('id, name')
      .ilike('name', categoryData.name)
      .single();

    if (existing) {
      return {
        success: false,
        error: `Kategoria "${categoryData.name}" już istnieje.`,
      };
    }

    const { data, error } = await supabase
      .from('book_categories')
      .insert({
        name: categoryData.name.trim(),
        description: categoryData.description?.trim() || null,
        color: categoryData.color || '#8B8D98',
        icon: categoryData.icon || null,
        parent_id: categoryData.parent_id || null,
        display_order: categoryData.display_order || 0,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating category:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje kategorię
 */
export async function updateCategory(
  categoryId: string,
  updates: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    parent_id?: string;
    display_order?: number;
  }
): Promise<{ success: boolean; data?: BookCategory; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('book_categories')
      .update(updates)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating category:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa kategorię
 */
export async function deleteCategory(categoryId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('book_categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting category:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// BOOK-TAG & BOOK-CATEGORY RELATIONS
// ========================================

/**
 * Przypisuje tagi do książki
 */
export async function assignTagsToBook(
  bookId: string,
  tagIds: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Usuń istniejące przypisania
    await supabase
      .from('book_tag_relations')
      .delete()
      .eq('book_id', bookId);

    // Dodaj nowe przypisania
    if (tagIds.length > 0) {
      const relations = tagIds.map(tagId => ({
        book_id: bookId,
        tag_id: tagId,
      }));

      const { error } = await supabase
        .from('book_tag_relations')
        .insert(relations);

      if (error) throw error;
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error assigning tags to book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Przypisuje kategorie do książki
 */
export async function assignCategoriesToBook(
  bookId: string,
  categoryIds: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Usuń istniejące przypisania
    await supabase
      .from('book_category_relations')
      .delete()
      .eq('book_id', bookId);

    // Dodaj nowe przypisania
    if (categoryIds.length > 0) {
      const relations = categoryIds.map(categoryId => ({
        book_id: bookId,
        category_id: categoryId,
      }));

      const { error } = await supabase
        .from('book_category_relations')
        .insert(relations);

      if (error) throw error;
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error assigning categories to book:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera tagi przypisane do książki
 */
export async function getBookTags(
  bookId: string
): Promise<{ success: boolean; data?: BookTag[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('book_tag_relations')
      .select('book_tags(*)')
      .eq('book_id', bookId);

    if (error) throw error;

    const tags = (data || [])
      .map((rel: any) => rel.book_tags)
      .filter(Boolean);

    return { success: true, data: tags };
  } catch (error: any) {
    console.error('Error fetching book tags:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera kategorie przypisane do książki
 */
export async function getBookCategories(
  bookId: string
): Promise<{ success: boolean; data?: BookCategory[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('book_category_relations')
      .select('book_categories(*)')
      .eq('book_id', bookId);

    if (error) throw error;

    const categories = (data || [])
      .map((rel: any) => rel.book_categories)
      .filter(Boolean);

    return { success: true, data: categories };
  } catch (error: any) {
    console.error('Error fetching book categories:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera książki z tagami i kategoriami (rozszerzona wersja getAllBooks)
 */
export async function getBooksWithTagsAndCategories(options?: {
  includeUnpublished?: boolean;
  tagIds?: string[];
  categoryIds?: string[];
  search?: string;
}): Promise<{ success: boolean; data?: BookWithTagsAndCategories[]; error?: string }> {
  try {
    let query = supabase
      .from('books')
      .select(`
        *,
        book_authors (
          author_order,
          authors (*)
        )
      `)
      .order('created_at', { ascending: false });

    // Filter by status
    if (!options?.includeUnpublished) {
      query = query.eq('status', 'published');
    }

    // Search
    if (options?.search) {
      query = query.or(
        `title.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    const { data: books, error: booksError } = await query;
    if (booksError) throw booksError;

    if (!books || books.length === 0) {
      return { success: true, data: [] };
    }

    // Pobierz tagi i kategorie dla wszystkich książek
    const bookIds = books.map(b => b.id);

    // Pobierz relacje tag-książka
    const { data: tagRelations } = await supabase
      .from('book_tag_relations')
      .select('book_id, book_tags(*)')
      .in('book_id', bookIds);

    // Pobierz relacje kategoria-książka
    const { data: categoryRelations } = await supabase
      .from('book_category_relations')
      .select('book_id, book_categories(*)')
      .in('book_id', bookIds);

    // Mapuj tagi i kategorie do książek
    const booksWithTagsAndCategories: BookWithTagsAndCategories[] = books.map((book: any) => {
      const bookTags = (tagRelations || [])
        .filter(rel => rel.book_id === book.id)
        .map(rel => rel.book_tags)
        .filter(Boolean);

      const bookCategories = (categoryRelations || [])
        .filter(rel => rel.book_id === book.id)
        .map(rel => rel.book_categories)
        .filter(Boolean);

      return {
        ...book,
        authors: (book.book_authors || [])
          .map((ba: any) => ({
            ...ba.authors,
            author_order: ba.author_order,
          }))
          .sort((a: any, b: any) => a.author_order - b.author_order),
        book_tags: bookTags,
        book_categories: bookCategories,
      };
    });

    // Filtruj po tagach (jeśli podane)
    let filteredBooks = booksWithTagsAndCategories;
    if (options?.tagIds && options.tagIds.length > 0) {
      filteredBooks = filteredBooks.filter(book =>
        book.book_tags?.some(tag => options.tagIds!.includes(tag.id))
      );
    }

    // Filtruj po kategoriach (jeśli podane)
    if (options?.categoryIds && options.categoryIds.length > 0) {
      filteredBooks = filteredBooks.filter(book =>
        book.book_categories?.some(cat => options.categoryIds!.includes(cat.id))
      );
    }

    return { success: true, data: filteredBooks };
  } catch (error: any) {
    console.error('Error fetching books with tags and categories:', error);
    return { success: false, error: error.message };
  }
}

